/**
 * MarketingDashboardPage.tsx
 *
 * Campaign Dashboard, the page the Marketing heading opens — 1:1 port of
 * campaign-dashboard.php. (The sidebar's "Marketing Dashboard" is the
 * Marketing Flex Table at /marketing/dashboard.)
 * Shows sandbox banner, page title, email/SMS/push stat groups (each with icon
 * shortcuts into its channel), and the recent campaign lists for each channel.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, AlertTriangle, Bell, Megaphone, Zap, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { Spinner } from '@/components/ui/Spinner';
import { StatCard } from '@/components/ui/StatCard';
import { cn } from '@/lib/utils';
import { PERMISSION } from '@/lib/permissions';
import { apiClient } from '@/services/apiClient';
import type { BadgeVariant } from '@/types/common.types';
import { usePageContext } from '@/hooks/usePageContext';
import { useAuth } from '@/hooks/useAuth';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailStats {
  totalCampaigns: number;
  sentCampaigns: number;
  totalRecipients: number;
  avgOpenRate: number;
}

interface SmsStats {
  totalCampaigns: number;
  sentCampaigns: number;
  totalRecipients: number;
  totalTemplates: number;
}

interface PushStats {
  totalCampaigns: number;
  sentCampaigns: number;
  totalRecipients: number;
  avgOpenRate: number;
}

/** Email, SMS and push recents all come back from /marketing/dashboard in this
 *  shape — drafts and deleted campaigns are already filtered out server-side. */
interface RecentCampaign {
  id: number;
  name: string;
  status: string;
  sentAt: string | null;
  scheduledAt: string | null;
  createdAt: string;
  totalRecipients: number;
}

interface DashboardData {
  sandboxEmail: boolean;
  sandboxSms: boolean;
  email: EmailStats;
  sms: SmsStats;
  push: PushStats;
  recentEmailCampaigns: RecentCampaign[];
  recentSmsCampaigns: RecentCampaign[];
  recentPushCampaigns: RecentCampaign[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'warning',
  sending: 'info',
  sent: 'success',
};

function numberFormat(n: number | undefined | null, decimals = 0): string {
  return (n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Legacy rows can carry '0000-00-00 00:00:00' or an empty date — render nothing
 *  rather than "Invalid Date". Space-separated MySQL datetimes are normalised so
 *  every browser parses them the same way. */
function fmtDate(d: string | null | undefined): string | null {
  if (!d || d.startsWith('0000')) return null;
  const date = new Date(d.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Sub-line under a campaign name: when it went out (or is due to) and how many
 *  contacts it reached. */
function campaignMeta(camp: RecentCampaign): string {
  const parts: string[] = [];
  const sent = fmtDate(camp.sentAt);
  const scheduled = fmtDate(camp.scheduledAt);
  if (sent) parts.push(`Sent ${sent}`);
  else if (scheduled) parts.push(`Scheduled ${scheduled}`);
  else {
    const created = fmtDate(camp.createdAt);
    if (created) parts.push(`Created ${created}`);
  }
  const n = camp.totalRecipients ?? 0;
  parts.push(`${numberFormat(n)} ${n === 1 ? 'contact' : 'contacts'}`);
  return parts.join(' · ');
}

// ─── Recent campaign row ─────────────────────────────────────────────────────

/** Name on its own line with the status badge, details underneath — keeps long
 *  campaign names readable instead of squeezing them against inline stats. The
 *  link wraps the whole row so the entire block is one click/tab target, with
 *  the same slate-50 hover the data tables use. */
function CampaignRow({ camp, href, hoverClass }: { camp: RecentCampaign; href: string; hoverClass: string }) {
  return (
    <li className="border-b border-slate-100 last:border-b-0">
      <Link
        to={href}
        className="group -mx-2 block rounded-md px-2 py-2.5 no-underline transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-start justify-between gap-2">
          <span className={cn('text-[13px] font-medium text-slate-800 [overflow-wrap:anywhere]', hoverClass)}>
            {camp.name}
          </span>
          <Badge variant={STATUS_BADGE[camp.status] ?? 'neutral'}>
            {camp.status.charAt(0).toUpperCase() + camp.status.slice(1)}
          </Badge>
        </div>
        <div className="mt-0.5 text-xs text-slate-500">{campaignMeta(camp)}</div>
      </Link>
    </li>
  );
}

// ─── Stat group header ───────────────────────────────────────────────────────

/** Icon shortcut in a stat group header. Every destination also lives in the
 *  sidebar; these are just the per-channel jump-offs. */
function HeaderLink({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 no-underline transition-colors hover:bg-slate-100 hover:text-slate-700"
    >
      <Icon className="h-4 w-4" />
    </Link>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function MarketingDashboardPage() {
  const updateAIContext = usePageContext('Campaign Dashboard');
  const { hasPermission } = useAuth();
  // Campaign / automation / push surfaces are separately permissioned; the
  // dashboard itself only needs Marketing Access, so hide what would bounce.
  const canSeeCampaigns = hasPermission(PERMISSION.MARKETING_CAMPAIGNS);
  const canSeeAutomations = hasPermission(PERMISSION.MARKETING_AUTOMATIONS);
  const canSeePush = hasPermission(PERMISSION.MARKETING_PUSH);

  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'dashboard'],
    queryFn: async () => {
      const res = await apiClient.get('/marketing/dashboard');
      return res.data.data as DashboardData;
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    updateAIContext({
      dataSummary: data
        ? `Email campaigns: ${data.email.totalCampaigns}, Email sent: ${data.email.sentCampaigns}, Email recipients: ${data.email.totalRecipients}, Email avg open rate: ${data.email.avgOpenRate}%, SMS campaigns: ${data.sms.totalCampaigns}, SMS sent: ${data.sms.sentCampaigns}, Push campaigns: ${data.push.totalCampaigns}, Push sent: ${data.push.sentCampaigns}, Push recipients: ${data.push.totalRecipients}, Push avg open rate: ${data.push.avgOpenRate}%, Recent email campaigns: ${data.recentEmailCampaigns.length}, Recent SMS campaigns: ${data.recentSmsCampaigns.length}`
        : 'Loading...',
    });
  }, [data, updateAIContext]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  const sandboxLabels: string[] = [];
  if (data.sandboxEmail) sandboxLabels.push('SendGrid Email');
  if (data.sandboxSms) sandboxLabels.push('Twilio SMS');

  return (
    <div className="w-full min-w-0 p-6 space-y-5">
      {/* ─── Sandbox Banner ──────────────────────────────────────── */}
      {sandboxLabels.length > 0 && (
        <div className="bg-orange-50 border border-orange-400 text-orange-900 text-center px-4 py-2.5 text-[13px] font-semibold rounded-md">
          <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />
          SANDBOX MODE ACTIVE &mdash; {sandboxLabels.join(' & ')} will not deliver to patients.
          To disable, set the sandbox constants to <code className="text-xs">false</code> in the config files.
        </div>
      )}

      {/* ─── Page Title ──────────────────────────────────────────── */}
      <h1 className="text-lg font-semibold text-slate-800">Campaign Dashboard</h1>

      {/* ─── Stats Row ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {/* Email Stats Group */}
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Mail className="h-[18px] w-[18px] text-sky-700" />
              <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide bg-sky-100 text-sky-700">
                Email
              </span>
            </div>
            <div className="flex items-center gap-0.5">
              {canSeeCampaigns && (
                <HeaderLink to="/marketing/email-campaigns" label="Email campaigns" icon={Megaphone} />
              )}
              {canSeeAutomations && (
                <HeaderLink to="/marketing/automations" label="Automations" icon={Zap} />
              )}
              <HeaderLink to="/marketing/templates" label="Templates" icon={FileText} />
            </div>
          </div>
          <MetricCardGrid columns={2}>
            <StatCard label="Campaigns" value={data.email.totalCampaigns} tone="sky" />
            <StatCard label="Sent" value={data.email.sentCampaigns} tone="blue" />
            <StatCard label="Recipients" value={data.email.totalRecipients} tone="teal" />
            <StatCard label="Avg Open Rate" value={`${numberFormat(data.email.avgOpenRate, 1)}%`} tone="green" />
          </MetricCardGrid>
        </Card>

        {/* SMS Stats Group */}
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-[18px] w-[18px] text-emerald-700" />
              <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">
                SMS
              </span>
            </div>
            <div className="flex items-center gap-0.5">
              {canSeeCampaigns && (
                <HeaderLink to="/marketing/sms-campaigns" label="SMS campaigns" icon={Megaphone} />
              )}
              {canSeeAutomations && (
                <HeaderLink to="/marketing/automations" label="Automations" icon={Zap} />
              )}
              <HeaderLink to="/marketing/templates" label="Templates" icon={FileText} />
            </div>
          </div>
          <MetricCardGrid columns={2}>
            <StatCard label="Campaigns" value={data.sms.totalCampaigns} tone="emerald" />
            <StatCard label="Sent" value={data.sms.sentCampaigns} tone="green" />
            <StatCard label="Recipients" value={data.sms.totalRecipients} tone="teal" />
            <StatCard label="Templates" value={data.sms.totalTemplates} tone="violet" />
          </MetricCardGrid>
        </Card>

        {/* Push Notifications Stats Group */}
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <Bell className="h-[18px] w-[18px] text-violet-700" />
              <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700">
                Push
              </span>
            </div>
            {/* Push has no automations or templates surface — campaigns only. */}
            {canSeePush && (
              <div className="flex items-center gap-0.5">
                <HeaderLink to="/marketing/push-campaigns" label="Push campaigns" icon={Megaphone} />
              </div>
            )}
          </div>
          <MetricCardGrid columns={2}>
            <StatCard label="Campaigns" value={data.push.totalCampaigns} tone="violet" />
            <StatCard label="Sent" value={data.push.sentCampaigns} tone="blue" />
            <StatCard label="Recipients" value={data.push.totalRecipients} tone="teal" />
            <StatCard label="Avg Open Rate" value={`${numberFormat(data.push.avgOpenRate, 1)}%`} tone="green" />
          </MetricCardGrid>
        </Card>
      </div>

      {/* ─── Recent Campaigns (side by side) ──────────────────────── */}
      {/* Every link in these panels lands on a permission-gated route, so the
          whole panel is hidden rather than left to bounce off the guard. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {/* Recent Email Campaigns */}
        {canSeeCampaigns && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-[17px] text-slate-800 mb-4 pb-2.5 border-b border-slate-200 flex items-center gap-2">
            <Mail className="h-[18px] w-[18px] text-sky-700" />
            Recent Email Campaigns
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-sky-100 text-sky-700">
              Email
            </span>
          </h2>
          {data.recentEmailCampaigns.length > 0 ? (
            <>
              <ul className="list-none p-0 m-0">
                {data.recentEmailCampaigns.map((camp) => (
                  <CampaignRow
                    key={camp.id}
                    camp={camp}
                    href={`/marketing/campaigns/${camp.id}`}
                    hoverClass="group-hover:text-sky-500"
                  />
                ))}
              </ul>
              <div className="text-center mt-3">
                <Link
                  to="/marketing/email-campaigns"
                  className="text-sky-500 no-underline text-[13px] hover:underline"
                >
                  View All Email Campaigns &rarr;
                </Link>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-center py-5 text-sm">
              No email campaigns sent or scheduled yet.{' '}
              <Link to="/marketing/email-campaigns" className="text-sky-500 no-underline hover:underline">
                Go to email campaigns
              </Link>
            </p>
          )}
        </div>
        )}

        {/* Recent SMS Campaigns */}
        {canSeeCampaigns && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-[17px] text-slate-800 mb-4 pb-2.5 border-b border-slate-200 flex items-center gap-2">
            <MessageSquare className="h-[18px] w-[18px] text-emerald-700" />
            Recent SMS Campaigns
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-700">
              SMS
            </span>
          </h2>
          {data.recentSmsCampaigns.length > 0 ? (
            <>
              <ul className="list-none p-0 m-0">
                {data.recentSmsCampaigns.map((camp) => (
                  <CampaignRow
                    key={camp.id}
                    camp={camp}
                    href="/marketing/sms-campaigns"
                    hoverClass="group-hover:text-emerald-600"
                  />
                ))}
              </ul>
              <div className="text-center mt-3">
                <Link
                  to="/marketing/sms-campaigns"
                  className="text-sky-500 no-underline text-[13px] hover:underline"
                >
                  View All SMS Campaigns &rarr;
                </Link>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-center py-5 text-sm">
              No SMS campaigns sent or scheduled yet.{' '}
              <Link to="/marketing/sms-campaigns" className="text-emerald-600 no-underline hover:underline">
                Go to SMS campaigns
              </Link>
            </p>
          )}
        </div>
        )}

        {/* Recent Push Campaigns */}
        {canSeePush && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-[17px] text-slate-800 mb-4 pb-2.5 border-b border-slate-200 flex items-center gap-2">
            <Bell className="h-[18px] w-[18px] text-violet-700" />
            Recent Push Campaigns
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-violet-100 text-violet-700">
              Push
            </span>
          </h2>
          {data.recentPushCampaigns.length > 0 ? (
            <>
              <ul className="list-none p-0 m-0">
                {data.recentPushCampaigns.map((camp) => (
                  <CampaignRow
                    key={camp.id}
                    camp={camp}
                    href="/marketing/push-campaigns"
                    hoverClass="group-hover:text-violet-600"
                  />
                ))}
              </ul>
              <div className="text-center mt-3">
                <Link
                  to="/marketing/push-campaigns"
                  className="text-violet-600 no-underline text-[13px] hover:underline"
                >
                  View All Push Campaigns &rarr;
                </Link>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-center py-5 text-sm">
              No push campaigns sent or scheduled yet.{' '}
              <Link to="/marketing/push-campaigns" className="text-violet-600 no-underline hover:underline">
                Go to push campaigns
              </Link>
            </p>
          )}
        </div>
        )}
      </div>

    </div>
  );
}
